import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { Plus, Edit, Trash2, Star, Download, Printer, MapPin } from "react-feather";
import {
  useCreateCustomerLocationMutation,
  useUpdateCustomerLocationMutation,
  useDeleteCustomerLocationMutation,
  useLazyGetCustomerLocationQrCodeQuery,
  useMarkQrLabelsPrintedMutation,
} from "../customersApi";
import { composeQrImage } from "../qrImageUtils";
import { blobToDataUrl, openStickerWindow, writeStickers } from "../qrStickerPrint";

const emptyForm = { siteLabel: "", address: "", isPrimary: false, isActive: true };

// Lazily fetches + composes one location's QR only when its modal is
// opened - printed at the office, stuck up at that specific site, so the
// image needs the site label in the title too (not just the customer name)
// or two sites belonging to the same customer become just as easy to
// mix up as two different customers were before this feature existed.
//
// Print (2026-09-17) produces the 70 x 40 mm roll sticker and records the
// print for this site (QR Stickers page).
const LocationQrModal = ({ branchId, custId, location, custName, custCode, customerLocation, onClose }) => {
  const [triggerQr] = useLazyGetCustomerLocationQrCodeQuery();
  const [markPrinted] = useMarkQrLabelsPrintedMutation();
  const [composedUrl, setComposedUrl] = useState(null);
  const [rawBlob, setRawBlob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handlePrint = async () => {
    if (!rawBlob) return;
    const win = openStickerWindow(1);
    if (!win) return;
    try {
      writeStickers(win, [
        {
          dataUrl: await blobToDataUrl(rawBlob),
          custName: (custName || `Customer ${custId}`).trim(),
          siteLabel: location.siteLabel,
          siteText: location.address || customerLocation,
          branchCode: location.branchCode,
          custCode,
        },
      ]);
      markPrinted([location.locationId]).catch(() => {});
    } catch {
      win.close();
      toast.error("Could not prepare the QR sticker");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      const { data: blob } = await triggerQr({ branchId, custId, locationId: location.locationId });
      if (!blob || cancelled) return;
      setRawBlob(blob);
      const url = await composeQrImage(blob, {
        title: `${custName} — ${location.siteLabel}`,
        subtitle: location.address,
      });
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      setComposedUrl(url);
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, custId, location.locationId]);

  useEffect(() => () => {
    if (composedUrl) URL.revokeObjectURL(composedUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{location.siteLabel} - QR Code</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {isLoading && <p className="text-muted mb-0">Loading...</p>}
        {composedUrl && (
          <img src={composedUrl} alt={`QR code for ${location.siteLabel}`} style={{ width: 220, border: "1px solid #eee", borderRadius: 4 }} />
        )}
      </Modal.Body>
      <Modal.Footer>
        {composedUrl && (
          <>
            <a
              href={composedUrl}
              download={`customer-${custId}-${location.siteLabel}-qr.png`}
              className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
            >
              <Download size={14} /> Download
            </a>
            <Button
              variant="outline-primary"
              className="d-inline-flex align-items-center gap-1"
              onClick={handlePrint}
            >
              <Printer size={14} /> Print
            </Button>
          </>
        )}
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Multi-location customers (handoff §19.6, added 2026-08-05) - a customer
// can have several genuinely distinct physical sites (house/office/parent's
// house), each with its own guard assignments and QR/scan tracking, while
// staying exactly one customer_master row - locations are a new child
// table, same "one parent, many children" shape guard assignments already
// used. The existing customer-level QR/location panels above this one on
// CustomerForm.jsx always mirror the PRIMARY location only and keep working
// unchanged for the (still by far most common) single-location case - this
// panel is where every *other* site lives.
const CustomerSitesPanel = ({ branchId, custId, custName, custCode, customerLocation, locations = [], readOnly }) => {
  const [createLocation, { isLoading: isCreating }] = useCreateCustomerLocationMutation();
  const [updateLocation] = useUpdateCustomerLocationMutation();
  const [deleteLocation] = useDeleteCustomerLocationMutation();

  const [showModal, setShowModal] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [qrLocation, setQrLocation] = useState(null);

  const sorted = [...locations].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));

  const openAdd = () => {
    setForm(emptyForm);
    setEditingLocationId(null);
    setShowModal(true);
  };

  const openEdit = (loc) => {
    setForm({ siteLabel: loc.siteLabel || "", address: loc.address || "", isPrimary: loc.isPrimary, isActive: loc.isActive });
    setEditingLocationId(loc.locationId);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.siteLabel.trim()) {
      toast.error("Site label is required (e.g. Home, Office)");
      return;
    }
    try {
      if (editingLocationId) {
        await updateLocation({
          branchId,
          custId,
          locationId: editingLocationId,
          siteLabel: form.siteLabel.trim(),
          address: form.address.trim() || null,
          isPrimary: form.isPrimary,
          isActive: form.isActive,
        }).unwrap();
      } else {
        await createLocation({
          branchId,
          custId,
          siteLabel: form.siteLabel.trim(),
          address: form.address.trim() || null,
          isPrimary: form.isPrimary,
        }).unwrap();
      }
      toast.success("Location saved");
      setShowModal(false);
    } catch (error) {
      toast.error(error?.message || "Could not save location");
    }
  };

  const handleSetPrimary = async (loc) => {
    try {
      await updateLocation({
        branchId,
        custId,
        locationId: loc.locationId,
        siteLabel: loc.siteLabel,
        address: loc.address,
        isPrimary: true,
        isActive: loc.isActive,
      }).unwrap();
      toast.success(`"${loc.siteLabel}" is now the primary location`);
    } catch (error) {
      toast.error(error?.message || "Could not set primary location");
    }
  };

  const handleToggleActive = async (loc, checked) => {
    try {
      await updateLocation({
        branchId,
        custId,
        locationId: loc.locationId,
        siteLabel: loc.siteLabel,
        address: loc.address,
        isPrimary: loc.isPrimary,
        isActive: checked,
      }).unwrap();
    } catch (error) {
      toast.error(error?.message || "Could not update location");
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Remove location "${loc.siteLabel}"? This cannot be undone.`)) return;
    try {
      await deleteLocation({ branchId, custId, locationId: loc.locationId }).unwrap();
      toast.success("Location removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove location");
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Locations</h5>
          {!readOnly && (
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={openAdd}>
              <Plus size={14} className="me-1" /> Add Location
            </button>
          )}
        </div>
        <p className="text-muted small">
          A customer can have more than one physical site (home, office, a second premises) - each gets its own
          QR code and guard assignments. Every customer starts with one location ("Main").
        </p>

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Address</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((loc) => (
                <tr key={loc.locationId}>
                  <td>
                    <span className="fw-semibold">{loc.siteLabel}</span>
                    {loc.isPrimary && (
                      <span className="badge badge-linesuccess ms-2 d-inline-flex align-items-center gap-1">
                        <Star size={11} /> Primary
                      </span>
                    )}
                  </td>
                  <td>{loc.address || "-"}</td>
                  <td>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={Boolean(loc.isActive)}
                        onChange={(e) => handleToggleActive(loc, e.target.checked)}
                        disabled={readOnly}
                      />
                    </div>
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-link p-1"
                        title="View / print QR code"
                        onClick={() => setQrLocation(loc)}
                      >
                        <MapPin size={15} />
                      </button>
                      {!readOnly && (
                        <>
                          {!loc.isPrimary && (
                            <button
                              type="button"
                              className="btn btn-link p-1"
                              title="Set as primary"
                              onClick={() => handleSetPrimary(loc)}
                            >
                              <Star size={15} />
                            </button>
                          )}
                          <button type="button" className="btn btn-link p-1" title="Edit" onClick={() => openEdit(loc)}>
                            <Edit size={15} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-link p-1 text-danger"
                            title={loc.isPrimary ? "The primary location can't be removed" : "Remove"}
                            onClick={() => handleDelete(loc)}
                            disabled={loc.isPrimary}
                          >
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted">No locations yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingLocationId ? "Edit Location" : "Add Location"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Site Label</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Home, Office, Warehouse"
              value={form.siteLabel}
              onChange={(e) => setForm((prev) => ({ ...prev, siteLabel: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Address</label>
            <input
              type="text"
              className="form-control"
              value={form.address}
              onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
            />
          </div>
          {editingLocationId && !form.isPrimary && (
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                id="loc-is-primary"
                checked={form.isPrimary}
                onChange={(e) => setForm((prev) => ({ ...prev, isPrimary: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="loc-is-primary">Make this the primary location</label>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isCreating}>Save</Button>
        </Modal.Footer>
      </Modal>

      {qrLocation && (
        <LocationQrModal
          branchId={branchId}
          custId={custId}
          custName={custName}
          custCode={custCode}
          customerLocation={customerLocation}
          location={qrLocation}
          onClose={() => setQrLocation(null)}
        />
      )}
    </div>
  );
};

export default CustomerSitesPanel;
